// Digital signature suites for identity authentication and message integrity.
// Provides classical (Ed25519), post-quantum (ML-DSA-65 / FIPS 204), and a
// hybrid that requires both to verify, for quantum-resistant transition safety.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ed25519 as nobleEd25519 } from '@noble/curves/ed25519.js';
import { ml_dsa65 } from '@noble/post-quantum/ml-dsa.js';
export const ed25519 = {
    name: 'ed25519',
    publicKeySize: 32,
    secretKeySize: 32,
    signatureSize: 64,
    generateKeyPair() {
        return __awaiter(this, void 0, void 0, function* () {
            const secretKey = nobleEd25519.utils.randomSecretKey();
            const publicKey = nobleEd25519.getPublicKey(secretKey);
            return { publicKey, secretKey };
        });
    },
    sign(message, secretKey) {
        return __awaiter(this, void 0, void 0, function* () {
            return nobleEd25519.sign(message, secretKey);
        });
    },
    verify(message, signature, publicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return nobleEd25519.verify(signature, message, publicKey);
            }
            catch (_a) {
                return false;
            }
        });
    }
};
export const mlDsa65 = {
    name: 'ml-dsa-65',
    publicKeySize: 1952,
    secretKeySize: 4032,
    signatureSize: 3309,
    generateKeyPair() {
        return __awaiter(this, void 0, void 0, function* () {
            return ml_dsa65.keygen();
        });
    },
    sign(message, secretKey) {
        return __awaiter(this, void 0, void 0, function* () {
            return ml_dsa65.sign(message, secretKey);
        });
    },
    verify(message, signature, publicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return ml_dsa65.verify(signature, message, publicKey);
            }
            catch (_a) {
                return false;
            }
        });
    }
};
function concatBytes(...arrays) {
    const totalLen = arrays.reduce((sum, a) => sum + a.length, 0);
    const result = new Uint8Array(totalLen);
    let offset = 0;
    for (const a of arrays) {
        result.set(a, offset);
        offset += a.length;
    }
    return result;
}
export const ed25519_mlDsa65 = {
    name: 'ed25519+ml-dsa-65',
    publicKeySize: ed25519.publicKeySize + mlDsa65.publicKeySize,
    secretKeySize: ed25519.secretKeySize + mlDsa65.secretKeySize,
    signatureSize: ed25519.signatureSize + mlDsa65.signatureSize,
    generateKeyPair() {
        return __awaiter(this, void 0, void 0, function* () {
            const classical = yield ed25519.generateKeyPair();
            const pq = yield mlDsa65.generateKeyPair();
            return {
                publicKey: concatBytes(classical.publicKey, pq.publicKey),
                secretKey: concatBytes(classical.secretKey, pq.secretKey),
            };
        });
    },
    sign(message, secretKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const classicalSk = secretKey.subarray(0, ed25519.secretKeySize);
            const pqSk = secretKey.subarray(ed25519.secretKeySize);
            const classicalSig = yield ed25519.sign(message, classicalSk);
            const pqSig = yield mlDsa65.sign(message, pqSk);
            return concatBytes(classicalSig, pqSig);
        });
    },
    verify(message, signature, publicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const classicalPk = publicKey.subarray(0, ed25519.publicKeySize);
            const pqPk = publicKey.subarray(ed25519.publicKeySize);
            const classicalSig = signature.subarray(0, ed25519.signatureSize);
            const pqSig = signature.subarray(ed25519.signatureSize);
            const classicalOk = yield ed25519.verify(message, classicalSig, classicalPk);
            const pqOk = yield mlDsa65.verify(message, pqSig, pqPk);
            return classicalOk && pqOk;
        });
    }
};
